<!-- House Lights (src/render-demo, the concept the boss saw on dev): a light page where every render
     sits on true black, and the room dims while one fills the screen. The header is /render's. -->
<div class="render-v2" #root>
  <header class="v2-head wrap">
    <a class="v2-mark" [href]="homeUrl" (click)="onMarkClick($event)" aria-label="3DWay">3DWay</a>
    <div class="v2-end">
      <nav class="v2-nav" aria-label="Page sections">
        <a *ngFor="let anchor of anchors" class="v2-nav__link" [href]="'#' + anchor.id" (click)="scrollTo(anchor.id, $event)">{{ anchor.label }}</a>
        <a class="btn-primary v2-cta" href="/render/upload" (click)="onStartClick($event)">Upload a model</a>
      </nav>
      <app-render-profile-menu></app-render-profile-menu>
    </div>
  </header>

  <main id="render-main" tabindex="-1">
    <!-- The copy in /render's type (Figma 28444:20590; designer, 2026-10-12: its fonts, icons and
         sizes, only the buttons black), this page's words. -->
    <section class="v2-hero wrap">
      <div class="v2-copy">
        <h1>Your work,<br>in its best light.</h1>
        <p>Turn your 3D model into a studio-lit film, framed twice: landscape and portrait from the same light and the same second.</p>
        <div class="v2-acts">
          <a class="btn-primary" href="/render/upload" (click)="onStartClick($event)">Upload your model</a>
          <a class="v2-text-link" href="#examples" (click)="scrollTo('examples', $event)">Selected works</a>
        </div>
      </div>

      <!-- One frame, two compositions: two videos on one timeline, the frame narrows from 16:9 to the
           portrait on the same second. -->
      <div class="morph" #morph [class.is-portrait]="format === 'portrait'">
        <div class="mframe">
          <video #morphLandscape class="ml" muted playsinline loop preload="none" [attr.data-hevc]="hero.landscape.hevc" [attr.data-h264]="hero.landscape.h264" [poster]="hero.landscape.poster" [attr.aria-label]="hero.sample.name + ', landscape'"></video>
          <video #morphPortrait class="mp" muted playsinline loop preload="none" [attr.data-hevc]="hero.portrait.hevc" [attr.data-h264]="hero.portrait.h264" [poster]="hero.portrait.poster" [attr.aria-label]="hero.sample.name + ', portrait'"></video>
        </div>
        <span class="mring" aria-hidden="true"></span>
        <div class="seg" role="group" aria-label="Format">
          <button type="button" [attr.aria-pressed]="format === 'landscape'" (click)="onFormatClick('landscape')">Landscape</button>
          <button type="button" [attr.aria-pressed]="format === 'portrait'" (click)="onFormatClick('portrait')">Portrait</button>
        </div>
        <div class="wl">
          <span class="wl-role">Model by</span>
          <span class="wl-t">{{ hero.sample.artist }}</span>
          <span class="wl-a">{{ hero.sample.name }} · {{ hero.sample.location }}</span>
        </div>
        <p class="msize"><span *ngFor="let item of sizeCharacters; let i = index" [style.animation-delay.ms]="i * 28">{{ item.character }}</span></p>
      </div>
    </section>

    <!-- /render's proof bar: the designer's three claims and her icons, inlined verbatim. -->
    <div class="v2-proof wrap">
      <div class="proof-item">
        <svg class="proof-icon" width="18" height="18" viewBox="0 0 17.5 17.5" fill="none" aria-hidden="true"><mask id="render-v2-proof-frame" fill="white"><rect x="4.75" y="4.75" width="8" height="8" rx="1"/></mask><rect x="4.75" y="4.75" width="8" height="8" rx="1" stroke="currentColor" stroke-width="3" mask="url(#render-v2-proof-frame)"/><path d="M16.75 11.75V15.75C16.75 16.3023 16.3023 16.75 15.75 16.75H11.75M16.75 5.75V1.75C16.75 1.19772 16.3023 0.75 15.75 0.75H11.75M0.75 5.75V1.75C0.75 1.19772 1.19772 0.75 1.75 0.75H5.75M0.75 11.75V15.75C0.75 16.3023 1.19772 16.75 1.75 16.75H5.75" stroke="currentColor" stroke-width="1.5"/></svg>
        <span><strong>Automatically framed</strong> for every angle</span>
      </div>
      <div class="proof-item">
        <svg class="proof-icon" width="19" height="15" viewBox="0 0 18.5 14.5" fill="none" aria-hidden="true"><path d="M0.75 2.75C0.75 1.64543 1.64543 0.75 2.75 0.75H10.75C11.8546 0.75 12.75 1.64543 12.75 2.75V7.75C12.75 8.85457 11.8546 9.75 10.75 9.75H2.75C1.64543 9.75 0.75 8.85457 0.75 7.75V2.75Z" stroke="currentColor" stroke-width="1.5"/><path d="M12.75 4.75C12.75 4.19772 13.1977 3.75 13.75 3.75H16.75C17.3023 3.75 17.75 4.19772 17.75 4.75V12.75C17.75 13.3023 17.3023 13.75 16.75 13.75H13.75C13.1977 13.75 12.75 13.3023 12.75 12.75V4.75Z" stroke="currentColor" stroke-width="1.5"/></svg>
        <span><strong>Portrait + landscape</strong> in one pack</span>
      </div>
      <div class="proof-item">
        <svg class="proof-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M1 8.94753L5.2 13.1475L15 3.34753" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
        <span><strong>Preview first.</strong> Pay when you like it</span>
      </div>
    </div>

    <section class="sec" id="examples">
      <div class="sec-head sec-head--split wrap">
        <h2>Good work deserves<br>a good presentation.</h2>
        <p class="muted">Wide, compact or tall. The same studio treatment, adapted to each model.</p>
      </div>
      <!-- On a phone each render plays its portrait film (designer, 2026-10-13), in a feed as the
           app's models feed: one swipe, one film (owner, 2026-10-13). -->
      <div class="bands" #feed [class.bands--feed]="portraitBands">
        <div *ngFor="let band of bands; trackBy: trackByBand" class="band" #band>
          <div class="band-media">
            <video muted playsinline loop preload="none" [attr.data-hevc]="bandReel(band).hevc" [attr.data-h264]="bandReel(band).h264" [poster]="bandReel(band).poster" [attr.aria-label]="band.sample.name + ' by ' + band.sample.artist"></video>
          </div>
          <div class="band-foot">
            <div class="band-credit">
              <span class="wl-role">Model by</span>
              <span class="bc-name">{{ band.sample.artist }}</span>
              <span class="bc-title">{{ band.sample.name }} · {{ band.sample.location }}</span>
            </div>
            <div class="band-acts">
              <!-- The sample's played upload, as on /render's upload step: the flow goes on to its preview. -->
              <button class="band-try" type="button" (click)="onTryClick(band.sample)">Try with this model<span aria-hidden="true">→</span></button>
              <!-- The render as a customer gets it (renderFullQuality): the page plays a lighter copy.
                   Under "Try with this model" (designer, 2026-11-03). -->
              <button *ngIf="bandReel(band).full as full" class="bc-full" type="button" (click)="openViewer(band, $event)">
                <svg width="8" height="10" viewBox="0 0 8 10" aria-hidden="true"><path d="M0 0v10l8-5z" fill="currentColor"/></svg>
                <span>Full quality · {{ fullLabel(full) }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="sec wrap v2-how" id="how-it-works">
      <div class="sec-head"><h2>From file to<br>finished film.</h2></div>
      <ol class="steps-row">
        <li *ngFor="let step of steps; let i = index">
          <span class="bign">{{ i + 1 }}</span>
          <div>
            <h3>{{ step.title }}</h3>
            <p>{{ step.text }}</p>
          </div>
        </li>
      </ol>
    </section>

    <section class="sec wrap spec-sec" id="specification">
      <div>
        <h2>Specification.</h2>
        <p class="muted">What every film comes with.</p>
      </div>
      <dl class="spec">
        <div><dt>Formats</dt><dd>{{ landscapeSize }} landscape · {{ portraitSize }} portrait</dd></div>
        <div><dt>Motion</dt><dd>{{ motion }}</dd></div>
        <div><dt>Versions</dt><dd>Source · Instagram &amp; TikTok · YouTube &amp; LinkedIn · ArtStation</dd></div>
        <div><dt>Input</dt><dd>FBX · USDZ, with textures</dd></div>
      </dl>
    </section>

    <!-- Pricing: /render's price card (Figma 28454:20085) in this page's spec-sheet type. -->
    <section class="sec wrap spec-sec price-sec" id="pricing">
      <div>
        <h2>One model.<br>One simple price.</h2>
        <p class="muted">Try the result before you buy it. No subscription.</p>
      </div>
      <div class="price-sheet">
        <div class="price-head">
          <span class="price-big">${{ price }}</span>
          <span class="price-per"><b>One-time payment</b><span>Both 360° films of one model</span></span>
        </div>
        <dl class="spec">
          <div><dt>Landscape</dt><dd>{{ landscapeSize }}</dd></div>
          <div><dt>Portrait</dt><dd>{{ portraitSize }}</dd></div>
          <div><dt>Preview</dt><dd>Free, before you pay</dd></div>
        </dl>
        <div class="spec-acts">
          <a class="btn-primary" href="/render/upload" (click)="onStartClick($event)">Upload your model</a>
          <span class="price-fine">{{ priceFine }}</span>
        </div>
      </div>
    </section>

    <!-- Laid out as the specification: the title left, the notes right (designer, 2026-10-12). -->
    <section class="sec wrap spec-sec notes-sec" id="questions">
      <div><h2>Studio notes.</h2></div>
      <div class="faq">
        <!-- The arrow is the account dialog's (designer, 2026-10-13: "наши стрелочки"). -->
        <details *ngFor="let note of notes">
          <summary>{{ note.question }}<img class="faq__chevron" src="assets/images/render/account/row-chevron.svg" width="16" height="16" alt="" /></summary>
          <p>{{ note.guide ? note.answer + ' ' : note.answer }}<button *ngIf="note.guide" class="faq__guide" type="button" (click)="openGuide()">Export guide</button></p>
        </details>
      </div>
    </section>

    <!-- Community: Original 3D Artists, our conversations on YouTube (Alex, 2026-10-09: the section
         leads to the channel). The Faces rail; the other variants are in src/render-demo/README.md. -->
    <section class="sec cm" id="community" aria-labelledby="community-title">
      <div class="sec-head sec-head--split wrap">
        <div>
          <p class="eyebrow cm-eyebrow">
            <svg class="cm-yt" width="22" height="16" viewBox="0 0 22 16" aria-hidden="true"><rect x="0.5" y="0.5" width="21" height="15" rx="4.5" fill="none" stroke="currentColor"/><path d="M9 5v6l5-3z" fill="currentColor"/></svg>
            Community · Original 3D Artists
          </p>
          <h2 class="cm-title" id="community-title">{{ episodes.length }} artists. In their own words.</h2>
        </div>
        <p class="muted">Original 3D Artists, our long conversations on YouTube. {{ subscribers }} subscribers.</p>
      </div>
      <div class="cm-rail-wrap">
        <ol class="cm-rail" #rail>
          <li *ngFor="let episode of episodes; let i = index; trackBy: trackByEpisode">
            <a class="cm-card" [href]="episodeUrl(episode)" target="_blank" rel="noopener" [attr.aria-label]="episode.guest + ', ' + episode.title + ', on YouTube'">
              <span class="face" [class.face--r]="episode.side === 'r'"><img [src]="episodeFrame(episode)" alt="" loading="lazy" decoding="async"></span>
              <span class="cm-n">{{ episodeNumber(i) }}&nbsp; · &nbsp;{{ episode.runtime }}</span>
              <span class="cm-cg">{{ episode.guest }}</span>
              <span class="cm-g">{{ episode.discipline }}</span>
            </a>
          </li>
        </ol>
      </div>
      <div class="wrap cm-foot">
        <div class="cm-acts">
          <a class="btn-ghost" [href]="channelUrl" target="_blank" rel="noopener">Watch on YouTube<span class="cm-arrow" aria-hidden="true">↗</span></a>
          <a class="link" [href]="subscribeUrl" target="_blank" rel="noopener">Subscribe</a>
        </div>
        <div class="cm-rail-nav">
          <button type="button" (click)="onRailClick(-1)" aria-label="Previous artists">←</button>
          <button type="button" (click)="onRailClick(1)" aria-label="Next artists">→</button>
        </div>
      </div>
    </section>
  </main>

  <!-- Figma 28839:46668 (designer, 2026-11-04): on a phone, centred on two lines. -->
  <footer class="foot wrap">
    <span class="foot-brand">3DWay</span>
    <span><a [href]="instagramUrl" target="_blank" rel="noopener">Instagram</a> · <a [href]="channelUrl" target="_blank" rel="noopener">YouTube</a><ng-container *ngFor="let doc of legalDocs"><span [class.foot-break]="doc.id === 'refunds'"> · </span><a [routerLink]="doc.url">{{ doc.label }}<span *ngIf="doc.id === 'privacy'" class="foot-more"> policy</span></a></ng-container><ng-container *ngIf="analytics.enabled"> · <button type="button" class="foot-link" (click)="analytics.openSettings()">Cookie settings</button></ng-container></span>
  </footer>

  <!-- "Full quality": the band's render as the file a customer gets, uncropped, both formats. -->
  <div *ngIf="viewer as view" class="fq" role="dialog" aria-modal="true" [attr.aria-label]="view.band.sample.name + ', full quality'" (keydown.tab)="onViewerTab($event)" (keydown.shift.tab)="onViewerTab($event)">
    <div class="fq-head">
      <div class="wl">
        <span class="wl-role">Model by</span>
        <span class="wl-t">{{ view.band.sample.artist }}</span>
        <span class="wl-a">{{ view.band.sample.name }} · {{ view.band.sample.location }}</span>
      </div>
      <div class="fq-acts">
        <div class="seg" role="group" aria-label="Format">
          <button type="button" [attr.aria-pressed]="view.format === 'landscape'" (click)="setViewerFormat('landscape')">Landscape</button>
          <button type="button" [attr.aria-pressed]="view.format === 'portrait'" (click)="setViewerFormat('portrait')">Portrait</button>
        </div>
        <button #viewerClose class="fq-close" type="button" aria-label="Close" (click)="closeViewer()">
          <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
        </button>
      </div>
    </div>
    <ng-container *ngIf="viewerFile(view) as file">
      <div class="fq-stage" (click)="$event.target === $event.currentTarget && closeViewer()">
        <video #viewerVideo class="fq-video" [class.fq-video--portrait]="view.format === 'portrait'" [src]="file.url" controls muted playsinline loop preload="auto" (loadedmetadata)="onViewerMetadata()"></video>
      </div>
      <p class="fq-spec"><span class="fq-as">As delivered</span>{{ viewerSpec(file) }}</p>
    </ng-container>
  </div>

  <!-- Whole-window drop target for signed-in users: a model dropped anywhere starts its preview. -->
  <app-render-drop-zone *ngIf="isSignedIn" mode="overlay" (filesCollected)="onFilesDropped($event)"></app-render-drop-zone>
</div>
